import type { Workflow } from '@compass/client/types/workflow';
import { workflowService } from '@services/api';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { WorkflowType } from '@/constants/dispositions';

/**
 * Lists the PQC workflows ("manufacturers") the current user can access across the
 * selected organization, for the manufacturer multi-select.
 *
 * Uses the org-scoped `ListOrganizationWorkflows` endpoint — one request,
 * backend-authorized to the user's accessible programs — instead of fanning out
 * `getWorkflowsByProgram` per program. The returned workflows carry `name` and
 * `sla.minutes`, so callers can build the per-row manufacturer-name / SLA maps the
 * cases table needs from this single response (no per-program fan-out).
 *
 * Returns the generated `Workflow` type directly (no custom projection).
 */
export const useOrgPqcWorkflows = (options?: { enabled?: boolean }) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const query = useQuery({
    queryKey: ['compass', 'tier2', 'org-workflows', selectedOrganizationId || '', WorkflowType.PQC],
    queryFn: () =>
      workflowService.listOrganizationWorkflows({
        organization_id: selectedOrganizationId!,
        workflow_type: WorkflowType.PQC,
      }),
    // Callers can gate the org-endpoint call (e.g. only for permission-eligible
    // users) via `enabled`; still requires a selected org.
    enabled: !!selectedOrganizationId && (options?.enabled ?? true),
    // Workflows rarely change during a session.
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const pqcWorkflows = useMemo<Workflow[]>(
    () => query.data?.workflows ?? [],
    [query.data?.workflows],
  );

  return {
    pqcWorkflows,
    isLoading: query.isLoading,
    error: query.error,
  };
};
